import { jwtDecode, jwtIsSalesChannel } from '@commercelayer/js-auth'

/** localStorage key — mirrors commercelayer/mfe-checkout `checkoutAccessToken`. */
export const OOB_CHECKOUT_ACCESS_TOKEN_KEY = 'oobCheckoutAccessToken'

/** Persisted order id for payment-gateway return after full page reload. */
export const OOB_CHECKOUT_ORDER_ID_KEY = 'oobCheckoutOrderId'

/**
 * Returns true only if the value is a valid Commerce Layer sales-channel JWT.
 * Mirrors the `jwtIsSalesChannel` check used in mfe-checkout `getSettings.ts`.
 */
export const isCommerceLayerSalesChannelJwt = (value: string): boolean => {
  try {
    const { payload } = jwtDecode(value)
    return jwtIsSalesChannel(payload)
  } catch {
    return false
  }
}

export const isCommerceLayerJwtExpired = (accessToken: string): boolean => {
  try {
    const { payload } = jwtDecode(accessToken)
    const exp = payload.exp
    if (typeof exp !== 'number') return false
    return Date.now() / 1000 >= exp
  } catch {
    return true
  }
}

/** Expiry (`exp` claim) of a Commerce Layer JWT as a Date, or null if absent/invalid. */
export const getCommerceLayerJwtExpiry = (accessToken: string): Date | null => {
  try {
    const { payload } = jwtDecode(accessToken)
    const exp = payload.exp
    if (typeof exp !== 'number') return null
    return new Date(exp * 1000)
  } catch {
    return null
  }
}

export const readStoredOobCheckoutAccessToken = (): string | null => {
  if (!import.meta.client || typeof localStorage === 'undefined') return null
  return localStorage.getItem(OOB_CHECKOUT_ACCESS_TOKEN_KEY)
}

export const readStoredOobCheckoutOrderId = (): string | null => {
  if (!import.meta.client || typeof localStorage === 'undefined') return null
  return localStorage.getItem(OOB_CHECKOUT_ORDER_ID_KEY)
}

export const writeStoredOobCheckoutAccessToken = (
  accessToken: string
): void => {
  if (!import.meta.client) return
  localStorage.setItem(OOB_CHECKOUT_ACCESS_TOKEN_KEY, accessToken)
}

export const writeStoredOobCheckoutOrderId = (orderId: string): void => {
  if (!import.meta.client) return
  localStorage.setItem(OOB_CHECKOUT_ORDER_ID_KEY, orderId)
}

export const clearStoredOobCheckoutAccessToken = (): void => {
  if (!import.meta.client) return
  localStorage.removeItem(OOB_CHECKOUT_ACCESS_TOKEN_KEY)
  localStorage.removeItem(OOB_CHECKOUT_ORDER_ID_KEY)
}

export const hasValidStoredOobCheckoutSession = (): boolean => {
  const accessToken = readStoredOobCheckoutAccessToken()
  const orderId = readStoredOobCheckoutOrderId()
  return Boolean(
    accessToken &&
      orderId &&
      isCommerceLayerSalesChannelJwt(accessToken) &&
      !isCommerceLayerJwtExpired(accessToken)
  )
}

export const hasOobCheckoutQueryParams = (
  query: Record<string, unknown>
): boolean =>
  typeof query.accessToken === 'string' &&
  query.accessToken.length > 0 &&
  typeof query.order_id === 'string' &&
  query.order_id.length > 0

/** OOB customer checkout entry via query params on /checkout. */
export const isOobCheckoutQuery = (query: Record<string, unknown>): boolean =>
  hasOobCheckoutQueryParams(query) &&
  isCommerceLayerSalesChannelJwt(query.accessToken as string)

/** Payment gateway return params (aligned with mfe-checkout useSettingsOrInvalid). */
export const isOobPaymentReturn = (query: Record<string, unknown>): boolean =>
  query.paymentReturn === 'true' ||
  typeof query.redirectResult === 'string' ||
  typeof query.payment_intent_client_secret === 'string'

export const shouldSkipDefaultCartInit = (
  path: string,
  query: Record<string, unknown>
): boolean => {
  if (!import.meta.client) return false

  if (path.includes('/checkout') && isOobCheckoutQuery(query)) {
    return true
  }

  if (path.includes('/adyen') || path.includes('/paypal')) {
    return hasValidStoredOobCheckoutSession()
  }

  return false
}
